import type { IntlShape } from 'react-intl'
import { defineMessages } from 'react-intl'

import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { formatMinutesToHoursMinutes } from 'lib/datetime/time'
import { uniqueBy } from 'lib/array/arrayUtils'
import { formatArrowSeparator, formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import type {
  LeCruiseSavedItemData,
  TransportConnection,
  TransportOption,
} from 'tripPlanner/api/types'
import {
  hasValidLatLng,
  MARKER_SOURCE_CRUISE_TRANSPORT,
  MARKER_TYPE_CRUISE_TRANSPORT,
  type MapMarkerComponent,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { CruiseTransportMapPoint } from 'tripPlanner/assistant/mapTypes'

export type CruiseScheduleDetails = NonNullable<TransportOption['cruiseSchedulePreview']>
type CruiseScheduleEndpoint = CruiseScheduleDetails['departure']

const messages = defineMessages({
  fallbackCruiseDuration: {
    id: 'V5szlLfNTCGk5jtppvm3',
    defaultMessage: '{count, plural, one {#-night cruise} other {#-night cruise}}',
    description: 'Fallback cruise duration shown when the transport option duration is missing',
  },
  scheduleDurationOvernight: {
    id: 'jc_29z21zjklKjV0XpFT',
    defaultMessage: '{duration}{separator}{nightCount, plural, one {Overnight} other {# nights}}',
    description: 'Cruise transport schedule duration label showing how many nights the sailing spans',
  },
  scheduleOvernightOnly: {
    id: 'qBaZe_UtXB_L4fVq8w_c',
    defaultMessage: '{nightCount, plural, one {Overnight} other {# nights}}',
    description: 'Cruise transport schedule label showing how many nights the sailing spans without a duration',
  },
})

function getValidTime(date: string | undefined): number | undefined {
  if (!date) return undefined
  const time = Date.parse(date)
  return Number.isFinite(time) ? time : undefined
}

export function getCruiseScheduleDetails(option: TransportOption): CruiseScheduleDetails | undefined {
  return option.cruiseSchedulePreview
}

export function formatCruiseDate(date: string | undefined, intl: IntlShape): string | undefined {
  const label = intlDateFormatter(date, INTL_SHORT_DAY_NAME_DAY_MONTH, intl.locale, 'UTC')
  return label || undefined
}

export function isOvernightSchedule(scheduleDetails: CruiseScheduleDetails): boolean {
  return getScheduleNightCount(scheduleDetails) !== undefined
}

function getScheduleNightCount(scheduleDetails: CruiseScheduleDetails): number | undefined {
  const departureTime = getValidTime(scheduleDetails.departure.localDate)
  const arrivalTime = getValidTime(scheduleDetails.arrival.localDate)
  if (departureTime === undefined || arrivalTime === undefined || arrivalTime <= departureTime) return undefined

  return Math.round((arrivalTime - departureTime) / (24 * 60 * 60 * 1000))
}

export function formatScheduleDuration(
  scheduleDetails: CruiseScheduleDetails,
  intl: IntlShape,
): string | undefined {
  const durationMinutes = scheduleDetails.durationMinutes
  const nightCount = getScheduleNightCount(scheduleDetails)

  if (durationMinutes !== undefined && nightCount !== undefined) {
    return intl.formatMessage(messages.scheduleDurationOvernight, {
      duration: formatMinutesToHoursMinutes(durationMinutes, intl),
      separator: formatMiddleDotSeparator(intl),
      nightCount,
    })
  }

  if (durationMinutes !== undefined) {
    return formatMinutesToHoursMinutes(durationMinutes, intl)
  }

  if (nightCount !== undefined) {
    return intl.formatMessage(messages.scheduleOvernightOnly, { nightCount })
  }
}

type CruiseScheduleEndpointWithCoordinates = CruiseScheduleEndpoint & {
  port: CruiseScheduleEndpoint['port'] & {
    latitude: number
    longitude: number
  }
}

function hasPortCoordinates(endpoint: CruiseScheduleEndpoint): endpoint is CruiseScheduleEndpointWithCoordinates {
  return hasValidLatLng(endpoint.port.latitude, endpoint.port.longitude)
}

export function getCruiseScheduleMapPoints(
  scheduleDetails: CruiseScheduleDetails | undefined,
): Array<CruiseTransportMapPoint> {
  if (!scheduleDetails) return []

  const points = [scheduleDetails.departure, scheduleDetails.arrival].flatMap((endpoint): Array<CruiseTransportMapPoint> => {
    if (!hasPortCoordinates(endpoint)) return []
    return [{
      name: endpoint.port.name,
      coordinates: {
        lat: endpoint.port.latitude,
        lng: endpoint.port.longitude,
      },
    }]
  })

  return uniqueBy(points, point => `${point.coordinates.lat}:${point.coordinates.lng}`)
}

export function createCruiseTransportMarkers(points: Array<CruiseTransportMapPoint>): Array<MapMarkerComponent> {
  return points.map((point, index) => ({
    ...point.coordinates,
    markerId: `cruise-transport-${index}-${point.coordinates.lat}:${point.coordinates.lng}`,
    label: point.name,
    title: point.name,
    markerType: MARKER_TYPE_CRUISE_TRANSPORT,
    source: MARKER_SOURCE_CRUISE_TRANSPORT,
  }))
}

export function getCruiseRouteName(
  transport: TransportConnection,
  savedItemData: LeCruiseSavedItemData,
  fallbackLabel: string,
  intl: IntlShape,
): string {
  if (transport.name) return transport.name
  const departurePort = savedItemData.departurePortName?.trim()
  const returnPort = savedItemData.returnPortName?.trim()
  if (departurePort && returnPort && departurePort !== returnPort) {
    return intl.formatMessage(
      {
        id: 'mRrVlUGaRgtApNY5xabV',
        defaultMessage: '{departurePort}{separator}{returnPort}',
        description: 'Cruise transport route label showing departure and return ports in the trip itinerary',
      },
      {
        departurePort,
        separator: formatArrowSeparator(intl),
        returnPort,
      },
    )
  }
  // ?.trim() || ..., not ?? -- matches getCruiseRouteNameEnglish below: an empty-string shipName/
  // cruiseLineName must fall through to the next option rather than short-circuit ?? (which only
  // falls through on null/undefined) and render a blank route label to the customer.
  return savedItemData.shipName?.trim() || savedItemData.cruiseLineName?.trim() || fallbackLabel
}

// Plain-English sibling of getCruiseRouteName, for activity displayText only -- that field is
// LLM-context only, never customer-facing, and stays English regardless of locale (same rule as
// every other kind in this family, e.g. buildItemStatusChangedActivityEntry's englishStatusLabel).
// getCruiseRouteName's own fallback chain calls intl.formatMessage for the distinct-ports case, and
// its fallbackLabel param is itself intl-derived at every call site -- both would leak a translated
// string into the activity entry whenever transport.name is empty, so this never touches intl.
export function getCruiseRouteNameEnglish(
  transport: TransportConnection,
  savedItemData: LeCruiseSavedItemData,
): string {
  if (transport.name) return transport.name
  const departurePort = savedItemData.departurePortName?.trim()
  const returnPort = savedItemData.returnPortName?.trim()
  if (departurePort && returnPort && departurePort !== returnPort) {
    return `${departurePort} to ${returnPort}`
  }
  // ?.trim() || ..., not ?? -- matches the guarding above, and an empty-string shipName/
  // cruiseLineName must fall through to the next option rather than short-circuit ?? (which only
  // falls through on null/undefined) and produce a blank name.
  return savedItemData.shipName?.trim() || savedItemData.cruiseLineName?.trim() || 'Cruise'
}

export function formatCruiseDuration(
  option: TransportOption,
  savedItemData: LeCruiseSavedItemData,
  intl: IntlShape,
): string | undefined {
  if (option.duration) return option.duration
  const numberOfNights = savedItemData.numberOfNights
  if (!numberOfNights || numberOfNights <= 0) return undefined
  return intl.formatMessage(messages.fallbackCruiseDuration, { count: numberOfNights })
}
